import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BrStrategic {
  booked_revenue_ytd: number | null
  annual_target: number | null
  gap_to_goal_value: number | null
  gap_to_goal_pct: number | null
  attainment_pct: number | null
  yoy_growth_pct: number | null
  target_not_configured: boolean
  waterfall: { nuovi_loghi: number; expansion: number; lost_value: number } | null
}

export interface PipelineStage {
  stage: string
  deal_count: number
  total_value: string          // Decimal serializzato — parsare con parseFloat
  weighted_value: string | null
  aging_median_days: number | null
  aged_count: number
}

export interface BrPipeline {
  weighted_pipeline: number | null
  pipeline_coverage_ratio: number | null
  sales_velocity_days: number | null
  aged_deals_count: number | null
  aged_deals_value: number | null
  aged_threshold_days: number
  pipeline_by_stage: PipelineStage[]
  data_gaps: string[]
}

export interface ReadinessOrder {
  id: string
  customer_name: string
  order_value: string          // Decimal serializzato — parsare con parseFloat
  status: string
  rag: 'red' | 'amber' | 'green'
  days_since_order: number
  days_no_activity: number
  cash_at_risk: string         // Decimal serializzato, "0" se non red
  blocco: string               // Descrizione italiana causa blocco, "" se green
}

export interface BrReadiness {
  backlog_total_value: number | null
  backlog_ready_value: number | null
  backlog_conversion_pct: number | null
  efficiency_score_days: number | null
  efficiency_score_available: boolean
  red_no_activity_days: number
  orders: ReadinessOrder[]
}

export interface BrRisk {
  code: string
  title: string
  severity: 'critical' | 'warning' | 'ok'
  value: number
  unit: string
  description: string
  drilldown_target: string
}

export interface BrDataConfidence {
  overall_score: number
  overall_rag: 'low' | 'medium' | 'high'
  recommendation: string
  layers: Record<string, { score: number; rag: string }>
}

export interface BrNarrative {
  content: string
  model: string
  generated_at: string
}

export interface BrSummary {
  period_start: string
  period_end: string
  generated_at: string
  strategic: BrStrategic
  pipeline: BrPipeline
  readiness: BrReadiness
  risks: BrRisk[]
  data_confidence: BrDataConfidence
  ai_narrative: BrNarrative | null
  narrative_status: 'ok' | 'unavailable' | 'disabled'
}

export interface BrTemplate {
  id: string
  name: string
  audience: string
  period_type: string
  is_default: boolean
}

export interface BrSnapshot {
  id: string
  tenant_id: string
  template_id: string | null
  period_label: string
  period_start: string
  period_end: string
  kpi_data: Record<string, unknown>
  data_confidence: Record<string, unknown>
  created_by: string | null
  created_at: string | null
  frozen_at: string | null
  is_frozen: boolean
}

export interface SnapshotCreateBody {
  period_label: string
  period_start: string   // YYYY-MM-DD
  period_end: string     // YYYY-MM-DD
  kpi_data?: Record<string, unknown>
  data_confidence?: Record<string, unknown>
  template_id?: string | null
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

interface SummaryParams {
  period_start: string
  period_end: string
  template_id?: string | null
  include_narrative?: boolean
}

export function useBusinessReviewSummary(params: SummaryParams) {
  const qs = new URLSearchParams({
    period_start: params.period_start,
    period_end: params.period_end,
  })
  if (params.template_id) qs.set('template_id', params.template_id)
  if (params.include_narrative === false) qs.set('include_narrative', 'false')

  return useQuery<BrSummary>({
    queryKey: ['br-summary', params.period_start, params.period_end, params.template_id ?? null],
    queryFn: () => api.get<BrSummary>(`/api/v1/business-review/summary?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useBusinessReviewTemplates() {
  return useQuery<BrTemplate[]>({
    queryKey: ['br-templates'],
    queryFn: () => api.get<BrTemplate[]>('/api/v1/business-review/templates'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useSnapshots(frozenOnly = false) {
  const qs = frozenOnly ? '?frozen_only=true' : ''
  return useQuery<BrSnapshot[]>({
    queryKey: ['br-snapshots', frozenOnly],
    queryFn: () => api.get<BrSnapshot[]>(`/api/v1/business-review/snapshots${qs}`),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateSnapshot() {
  const qc = useQueryClient()
  return useMutation<BrSnapshot, Error, SnapshotCreateBody>({
    mutationFn: (body) => api.post<BrSnapshot>('/api/v1/business-review/snapshots', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['br-snapshots'] }),
  })
}

export function useFreezeSnapshot() {
  const qc = useQueryClient()
  return useMutation<BrSnapshot, Error, string>({
    mutationFn: (snapshotId) =>
      api.post<BrSnapshot>(`/api/v1/business-review/snapshots/${snapshotId}/freeze`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['br-snapshots'] }),
  })
}
